import React from "react";
import { commitMutation, graphql, useFragment, useRelayEnvironment } from "react-relay";

import { faChevronLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import Avatar from "~/ui/avatar/Avatar";
import AvatarList from "~/ui/avatar/AvatarList";
import { Button } from "~/ui/button";
import IconButton from "~/ui/button/IconButton";
import Modal from "~/ui/Modal";
import { useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import type { ShareRequests_noteAccessRequests$key } from "~/__generated__/ShareRequests_noteAccessRequests.graphql";
import type { ShareRequestsApproveMutation } from "~/__generated__/ShareRequestsApproveMutation.graphql";
import type { ShareRequestsDeclineMutation } from "~/__generated__/ShareRequestsDeclineMutation.graphql";

import styles from "./css/ShareRequests.module.css";

const approveAccessRequestMutation = graphql`
	mutation ShareRequestsApproveMutation($accessRequestId: ID!) {
		approveNoteAccessRequest(accessRequestId: $accessRequestId) {
			note {
				id
				...ShareRequests_noteAccessRequests
				...ShareMeetingModal_sharedWith
			}
		}
	}
`;

const declineAccessRequestMutation = graphql`
	mutation ShareRequestsDeclineMutation($accessRequestId: ID!) {
		declineNoteAccessRequest(accessRequestId: $accessRequestId) {
			note {
				id
				...ShareRequests_noteAccessRequests
				...ShareMeetingModal_sharedWith
			}
		}
	}
`;

const noteAccessRequestsFragment = graphql`
	fragment ShareRequests_noteAccessRequests on Note {
		pendingAccessRequests {
			id
			createdAt
			requestor {
				id
				fullName
				email
				image
				isExternal
			}
		}
	}
`;

function RequestsModal({
	note,
	close,
	isLoading,
	onApproveRequest,
	onDeclineRequest,
}: {
	note: ShareRequests_noteAccessRequests$key;
	close: () => void;
	isLoading: boolean;
	onApproveRequest: (requestId: string) => () => void;
	onDeclineRequest: (requestId: string) => () => void;
}) {
	const { pendingAccessRequests } = useFragment<ShareRequests_noteAccessRequests$key>(noteAccessRequestsFragment, note);

	// Close modal when there are no more pending requests
	React.useEffect(() => {
		if (pendingAccessRequests.length === 0) {
			close();
		}
	}, [pendingAccessRequests.length, close]);

	return (
		<Modal onClose={close}>
			<Modal.Header onClose={close}>
				<Modal.Title className={styles.requestModalTitle}>
					<IconButton variant="tertiary" icon={faChevronLeft} onClick={close} />

					<span>Requests for access</span>
				</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				{pendingAccessRequests.map(request => (
					<div key={request.id} className={styles.requestModalRow}>
						<div className={styles.requestUser}>
							<Avatar user={request.requestor} className={styles.avatar} />

							<div className={styles.requestUserText}>
								<b>{request.requestor.fullName}</b>
							</div>
						</div>

						<div className={styles.requestButtons}>
							<Button variant="outlined" size="slim" onClick={onDeclineRequest(request.id)} disabled={isLoading}>
								Deny
							</Button>
							<Button variant="primary" size="slim" onClick={onApproveRequest(request.id)} disabled={isLoading}>
								Approve
							</Button>
						</div>
					</div>
				))}
			</Modal.Body>
		</Modal>
	);
}

export default function ShareRequests({ note }: { note: ShareRequests_noteAccessRequests$key }) {
	const { pendingAccessRequests } = useFragment<ShareRequests_noteAccessRequests$key>(noteAccessRequestsFragment, note);
	const [isLoading, setIsLoading] = React.useState(false);
	const modals = useModals();
	const environment = useRelayEnvironment();

	const onApproveRequest = React.useCallback(
		(requestId: string) => () => {
			if (isLoading) return;

			setIsLoading(true);

			commitMutation<ShareRequestsApproveMutation>(environment, {
				mutation: approveAccessRequestMutation,
				variables: { accessRequestId: requestId },
				onCompleted: ({ approveNoteAccessRequest }, errors) => {
					if (errors?.length || !approveNoteAccessRequest?.note) {
						toast.error("Unable to approve this request");
					} else {
						toast.success("Request approved");
					}

					setIsLoading(false);
				},
			});
		},
		[isLoading, environment],
	);

	const onDeclineRequest = React.useCallback(
		(requestId: string) => () => {
			if (isLoading) return;

			setIsLoading(true);

			commitMutation<ShareRequestsDeclineMutation>(environment, {
				mutation: declineAccessRequestMutation,
				variables: { accessRequestId: requestId },
				onCompleted: ({ declineNoteAccessRequest }, errors) => {
					if (errors?.length || !declineNoteAccessRequest?.note) {
						toast.error("Unable to decline this request");
					} else {
						toast.success("Request declined");
					}

					setIsLoading(false);
				},
			});
		},
		[isLoading, environment],
	);

	const openRequestsModal = React.useCallback(() => {
		modals.open(({ close }) => (
			<RequestsModal
				note={note}
				close={close}
				isLoading={isLoading}
				onApproveRequest={onApproveRequest}
				onDeclineRequest={onDeclineRequest}
			/>
		));
	}, [note, isLoading, onApproveRequest, onDeclineRequest, modals]);

	if (pendingAccessRequests.length === 0) {
		return null;
	}

	let content = null;

	if (pendingAccessRequests.length === 1) {
		const request = pendingAccessRequests[0];

		content = (
			<div className={styles.requestRow}>
				<div className={styles.requestUser}>
					<Avatar user={request.requestor} className={styles.avatar} />

					<div className={styles.requestUserText}>
						<span className={styles.textEmphasis}>{request.requestor.fullName}</span> has requested access
					</div>
				</div>

				<div className={styles.requestButtons}>
					<Button variant="outlined" size="slim" onClick={onDeclineRequest(request.id)} disabled={isLoading}>
						Deny
					</Button>
					<Button variant="primary" size="slim" onClick={onApproveRequest(request.id)} disabled={isLoading}>
						Approve
					</Button>
				</div>
			</div>
		);
	} else {
		const users = pendingAccessRequests.map(request => request.requestor);

		content = (
			<div className={styles.requestRow}>
				<div className={styles.requestUser}>
					<AvatarList users={users} size="medium" maxLength={3} />

					<div className={styles.requestUserText}>
						<span className={styles.textEmphasis}>{users.length} people</span> have requested access
					</div>
				</div>

				<Button variant="primary" size="slim" onClick={openRequestsModal}>
					Review
				</Button>
			</div>
		);
	}

	return <div className={styles.requestsWrapper}>{content}</div>;
}
